import {Modal, Spin} from "antd";
import mime from "mime";
import {ReactElement, useEffect, useState} from "react";
import {ErrorInUploadedFile, renameFile} from "src/common/utils/file-utils";
import {iif} from "src/common/utils/functions";
import {uploadFileViaGcsSignedUrl} from "src/components/service/gcs-signed-url";
import {successMsg} from "src/components/simpleMessageAlert";
import "./index.scss";
import {
  deletedUploadedFilesInGcs,
  getRequiredUploadsForApplicationFiles,
  getUploadDetailsForReviewCycleFile,
  updateUploadedFileNamesForApp,
} from "./service";
import axios from "axios";
import {RequiredUploads} from "./model/required-uploads";
import {RequiredFile} from "./model/required-file";
import {UploadedFileDetails} from "./model/uploaded-file";
import {useDispatch} from "react-redux";
import {ApplicationListPageActions} from "../applicationList/model";
import {Nullable} from "../../../common/utils/nullable";
import ErrorModal from "../../../components/error-modal";
import {ErrorResponse, FindErrorResponseType, getErrorResponse} from "../../../common/errors";
import {ReviewAction, sendReviewEvent} from "../../../components/matomoClient";

type Props = {
  modalTriggerElement: ReactElement;
  appInfo: { name: string; id: string; hasUploadPermission: boolean; };
};

type ButtonProps = {
  loading: boolean;
  disabled: boolean;
};

function UploadReviewCycleFiles(props: Props) {
  const axiosCancelRequestsToken = axios.CancelToken.source();
  const [isOpen, setIsOpen] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<Array<UploadedFileDetails>>([]);
  const [requiredUploads, setRequiredUploads] =
    useState<Nullable<RequiredUploads>>();
  const [submitButtonProps, setSubmitButtonProps] = useState<ButtonProps>({
    loading: false,
    disabled: true,
  });
  const [errorResponse, setErrorResponse] = useState<Nullable<ErrorResponse>>(null);
  const [error, setError] = useState<Nullable<string>>(null);
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [isContentLoading, setIsContentLoading] = useState<boolean>(false);
  const dispatch = useDispatch()

  async function handleUpload(
    fileToBeUploaded: File,
    fileInfo: RequiredFile,
    setProgress: (progress: number) => void
  ): Promise<File> {
    sendReviewEvent(ReviewAction.UploadedFile, `For ${fileInfo.displayName} of ${props.appInfo.name}`)
    try {
      const uploadInfo = await getUploadDetailsForReviewCycleFile(
        props.appInfo.id,
        props.appInfo.name,
        fileInfo.fileCategory,
        mime.getExtension(fileToBeUploaded.type) ?? ""
      );
      const renamedFile = renameFile(fileToBeUploaded, uploadInfo.fileName);
      await uploadFileViaGcsSignedUrl(uploadInfo.signedUrl, renamedFile, setProgress, {
        cancelToken: axiosCancelRequestsToken.token,
      });
      setUploadedFiles((previousValue) => {
        return [
          ...previousValue,
          new UploadedFileDetails(
            props.appInfo.name,
            renamedFile.name,
            fileInfo.fileCategory,
            fileInfo.id
          ),
        ];
      });
      return renamedFile;
    } catch (error) {
      throw new ErrorInUploadedFile("Upload Unsuccessful");
    }
  }

  async function onSubmit() {
    setErrorResponse(null)
    setSubmitButtonProps({...submitButtonProps, loading: true});
    sendReviewEvent(ReviewAction.SubmitFiles, props.appInfo.name)
    try {
      await updateUploadedFileNamesForApp(
        props.appInfo.id,
        uploadedFiles.map((uploadedFile: UploadedFileDetails) => uploadedFile.submitFilePayload())
      ).then(x => dispatch(ApplicationListPageActions.uploadFileSubmit({
        appId: props.appInfo.id,
        currentStatus: x.currentStatus,
        applicableActions: x.applicableActions
      })));
      successMsg("Successfully submitted the files");
      setIsOpen(false);
      setErrorResponse(null);
    } catch (err) {
      iif(function setErrorBasedOnStatusCode() {
        if (FindErrorResponseType.isClientError(err)) {
          setErrorResponse(getErrorResponse(err));
          setIsErrorModalVisible(true);
          return;
        }
        setError("Error submitting files. Please try again")
      })
      if (FindErrorResponseType.isServerError(err)) {
        deleteUploadedFiles()
      }
      await fetchRequiredUploads();
    }
    setUploadedFiles([])
    setSubmitButtonProps({disabled: true, loading: false});
  }

  function fetchRequiredUploads() {
    if (isOpen) {
      setIsContentLoading(true)
      setRequiredUploads(null);
      iif(async () => {
        try {
          setRequiredUploads(await getRequiredUploadsForApplicationFiles(props.appInfo.id));
        } catch (_err) {
          setError("Error fetching upload details");
        }
        setIsContentLoading(false)
      }).then();
    } else {
      setRequiredUploads(null);
    }
  }

  function areAllMandatoryFilesUploaded(): boolean {
    return requiredUploads?.areMandatoryFilesUploaded(
      uploadedFiles.map((uploadedFile: UploadedFileDetails) => uploadedFile.id)
    ) ?? false
  }

  function onFileUpload() {
    setSubmitButtonProps((prevState) => ({
      ...prevState,
      disabled: !(uploadedFiles.length > 0 && areAllMandatoryFilesUploaded()),
    }));
  }

  function deleteUploadedFiles() {
    if (uploadedFiles.length > 0) {
      deletedUploadedFilesInGcs({
        deleteFileInfos: uploadedFiles.map((uploadedFile) => uploadedFile.deleteFilePayload()),
      }).then();
    }
    setUploadedFiles([]);
  }

  function onCancel() {
    axiosCancelRequestsToken.cancel();
    deleteUploadedFiles();
    setIsOpen(false);
    setErrorResponse(null);
    setError(null);
    setIsErrorModalVisible(false);
    sendReviewEvent(ReviewAction.CloseUpload, props.appInfo.name)
  }

  function onModalTriggerElementClick() {
    setIsOpen(true);
    sendReviewEvent(ReviewAction.OpenUpload, props.appInfo.name)
  }

  function onUnMounting() {
    if (axiosCancelRequestsToken) {
      axiosCancelRequestsToken.cancel();
    }
  }

  useEffect(fetchRequiredUploads, [isOpen]);

  useEffect(onFileUpload, [uploadedFiles]);

  useEffect(() => {
    return onUnMounting;
  }, []);

  return (
    <div className={"UploadReviewCycleFiles"}>
      <div
        onClick={onModalTriggerElementClick}
      >
        {props.appInfo.hasUploadPermission ? props.modalTriggerElement : null}
      </div>
      <Modal
        className="upload-review-cycle-files-modal"
        visible={isOpen}
        maskClosable={false}
        onCancel={onCancel}
        width={"auto"}
        title={`Upload Files - ${props.appInfo.name}`}
        centered={true}
        okText={"Submit"}
        okButtonProps={submitButtonProps}
        closable={false}
        onOk={onSubmit}
      >
        {error && <div className="error-container">{error}</div>}
        {isContentLoading ? <Spin className="spin-container"/> : null}
        <ErrorModal
          isVisible={isErrorModalVisible}
          setVisibility={setIsErrorModalVisible}
          error={errorResponse}
          appName={props.appInfo.name}
        />
        <div className="files-upload-container">
          {requiredUploads?.render(handleUpload) ?? null}
        </div>
      </Modal>
    </div>
  );
}

export default UploadReviewCycleFiles;
